/* eslint-disable brace-style */
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, Validators, ValidatorFn } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import {
  AbstractFormDialog,
  AbstractFormDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';

import { EnvironmentInjector } from '@next/core-lib/environment';
import { SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { getCropYear, selectCropFieldAdvicesByIds } from 'src/app/store/selectors';
import { Environment } from 'src/environments/environment';

import {
  FertilizationSetting,
  CropData,
  NitrogenCalculationSettingToggles,
  OrganicMatterSupply,
  FertilizationSettingWithName,
  FertilizationSettingWithIds,
  CropFieldAdvice,
} from '../../../shared/models';
import {
  selectNitrogenCalculationSettingToggles,
} from '../../store/selectors';

export interface FertilizationSettingDialogModel extends AbstractFormDialogData {
  fertilizationSetting?: FertilizationSettingWithIds;
  crops: CropData[];
  nonProductiveCrops: CropData[];
  greenManureCrops: CropData[];
  cropId: number;
}

@Component({
  selector: 'app-fertilization-setting-dialog',
  templateUrl: './fertilization-setting-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FertilizationSettingDialogComponent
  extends AbstractFormDialog<FertilizationSetting>
  implements OnInit, OnDestroy
{
  data = inject<FertilizationSettingDialogModel>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<FertilizationSettingDialogComponent, AbstractDialogResultData<FertilizationSetting>>>(MatDialogRef);
  private readonly env = inject<Environment>(EnvironmentInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  organicMatterSupplyKeys: string[];

  nitrogenCalculationSettingToggles$ = this.store.pipe(
    select(selectNitrogenCalculationSettingToggles),
  );

  cropId$: Observable<number>;
  cropYear$ = this.store.pipe(select(getCropYear));

  greenManureFormGroup = this.formBuilder.group({
    cropId: [this.data.fertilizationSetting.greenManureSettings?.cropId],
    seasonGreenManureIncorporated: [
      this.data.fertilizationSetting.greenManureSettings?.seasonGreenManureIncorporated,
    ],
    successRate: [this.data.fertilizationSetting.greenManureSettings?.successRate],
    isNitrogenDeliveryAmountSetManually: [
      this.data.fertilizationSetting.greenManureSettings?.isNitrogenDeliveryAmountSetManually,
    ],
    nitrogenDeliveryAmount: [
      this.data.fertilizationSetting.greenManureSettings?.nitrogenDeliveryAmount,
    ],
  });

  precedingCropFormGroup = this.formBuilder.group({
    cropId: [this.data.fertilizationSetting.precedingCropSettings?.cropId],
    isNitrogenDeliveryAmountSetManually: [
      this.data.fertilizationSetting.precedingCropSettings?.isNitrogenDeliveryAmountSetManually,
    ],
    nitrogenDeliveryAmount: [
      this.data.fertilizationSetting.precedingCropSettings?.nitrogenDeliveryAmount,
    ],
  });

  nitrogenCalculationSettingsFormGroup = this.formBuilder.group({
    isPreviousCropCorn: [
      this.data.fertilizationSetting?.nitrogenCalculationSettings?.isPreviousCropCorn ?? false,
    ],
    isPreviousCropNotGrain: [
      this.data.fertilizationSetting?.nitrogenCalculationSettings?.isPreviousCropNotGrain ?? false,
    ],
    grasslandUsage: [this.data.fertilizationSetting?.nitrogenCalculationSettings?.grasslandUsage],
    shouldSplitNorm: [
      this.data.fertilizationSetting?.nitrogenCalculationSettings?.shouldSplitNorm ?? false,
    ],
    isNitrogenDifferentiation: [
      this.data.fertilizationSetting?.nitrogenCalculationSettings?.isNitrogenDifferentiation ??
        false,
    ],
    isAfterTornGrass: [
      this.data.fertilizationSetting?.nitrogenCalculationSettings?.isAfterTornGrass ?? false,
    ],
    isRecoveryFertilisation: [this.data.fertilizationSetting?.nitrogenCalculationSettings?.isRecoveryFertilisation ?? false],
  });

  formGroup = this.formBuilder.group({
    cropId: [this.data.cropId],
    isPrecedingCrop: [this.data.fertilizationSetting.isPrecedingCrop],
    isGreenManure: [this.data.fertilizationSetting.isGreenManure],
    greenManureSettings: this.greenManureFormGroup,
    precedingCropSettings: this.precedingCropFormGroup,
    nitrogenCalculationSettings: this.nitrogenCalculationSettingsFormGroup,
    shouldBeUsedInUsageStandard: [this.data.fertilizationSetting.shouldBeUsedInUsageStandard],
    shouldBeUsedInLegalStandard: [this.data.fertilizationSetting.shouldBeUsedInLegalStandard],
    shouldUseTransitionalArrangement: [
      this.data.fertilizationSetting.shouldUseTransitionalArrangement,
    ],
    shouldUseLowInPhosphateArrangement: [
      this.data.fertilizationSetting.shouldUseLowInPhosphateArrangement,
    ],
    organicMatterSupply: [this.data.fertilizationSetting.organicMatterSupply],
    expectedCropYield: [
      this.data.fertilizationSetting.expectedCropYield,
      [Validators.required, Validators.min(0), Validators.max(1000000000)],
    ],
  });

  ngOnInit() {
    this.registerFormEvents();

    this.organicMatterSupplyKeys = Object.keys(OrganicMatterSupply);

    this.setIsAfterTornGrassAutomatically();
    this.setShouldBeUsedInLegalStandardAutomatically();

    this.cropId$ = this.formGroup.controls.cropId.valueChanges.pipe(
      startWith(this.formGroup.controls.cropId.value),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setIsAfterTornGrassAutomatically() {
    const isPrecedingCrop$ = this.formGroup.get('isPrecedingCrop');
    const cropId$ = this.precedingCropFormGroup.get('cropId');

    combineLatest([
      this.store.pipe(
        select(selectNitrogenCalculationSettingToggles),
        startWith({ shouldShowAfterTornGrassToggle: false }),
      ),
      isPrecedingCrop$.valueChanges.pipe(startWith(isPrecedingCrop$.value)),
      cropId$.valueChanges.pipe(startWith(cropId$.value)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        ([nitrogenCalculationSettingToggles, isPrecedingCrop, cropId]: [
          NitrogenCalculationSettingToggles,
          boolean,
          number,
        ]) => {
          if (
            nitrogenCalculationSettingToggles?.shouldShowAfterTornGrassToggle &&
            isPrecedingCrop
          ) {
            this.nitrogenCalculationSettingsFormGroup
              .get('isAfterTornGrass')
              .setValue(cropId === this.env.GRASSLAND_CROP_ID);
          }
        },
      );
  }

  setShouldBeUsedInLegalStandardAutomatically() {
    this.store
      .pipe(select(selectCropFieldAdvicesByIds([this.data.fertilizationSetting.adviceId])))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cropFieldAdvices: CropFieldAdvice[]) => {
        if (
          cropFieldAdvices.length > 0 &&
          this.data.nonProductiveCrops?.some((c) => c.id === cropFieldAdvices[0].crop?.id)
        ) {
          this.formGroup.get('shouldBeUsedInLegalStandard').setValue(false);
          this.formGroup.get('shouldBeUsedInLegalStandard').disable();
        }
      });
  }

  isPrecedingCrop() {
    return this.formGroup.get('isPrecedingCrop').value;
  }

  isGreenManure() {
    return this.formGroup.get('isGreenManure').value;
  }

  shouldBeUsedInUsageStandard() {
    return this.formGroup.get('shouldBeUsedInUsageStandard').value;
  }

  shouldBeUsedInLegalStandard() {
    return this.formGroup.get('shouldBeUsedInLegalStandard').value;
  }

  shouldUseTransitionalArrangement() {
    return this.formGroup.get('shouldUseTransitionalArrangement').value;
  }

  shouldUseLowInPhosphateArrangement() {
    return this.formGroup.get('shouldUseLowInPhosphateArrangement').value;
  }

  confirm($event: FertilizationSettingWithName) {
    if (!this.isGreenManure()) {
      $event.greenManureSettings = null;
    }

    if (!this.isPrecedingCrop()) {
      $event.precedingCropSettings = null;
    }

    super.confirm($event);
  }

  private registerFormEvents() {
    this.formGroup
      .get('isGreenManure')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((isGreenManure: boolean) => {
        this.updateGreenManureForm(isGreenManure);
      });

    this.formGroup
      .get('isPrecedingCrop')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((isPrecedingCrop: boolean) => {
        this.updatePrecedingCropForm(isPrecedingCrop);

        this.precedingCropFormGroup
          .get('isNitrogenDeliveryAmountSetManually')
          .setValue(false, { emitEvent: false });
      });

    this.updateGreenManureForm(!!this.formGroup.get('isGreenManure').value);
    this.updatePrecedingCropForm(!!this.formGroup.get('isPrecedingCrop').value);
  }

  private setGreenManureSettingsValidator(newValidator: ValidatorFn[]) {
    this.greenManureFormGroup.get('cropId').setValidators(newValidator);
    this.greenManureFormGroup.get('seasonGreenManureIncorporated').setValidators(newValidator);
    this.greenManureFormGroup.get('successRate').setValidators(newValidator);
    this.greenManureFormGroup
      .get('nitrogenDeliveryAmount')
      .setValidators([...newValidator, Validators.min(1)]);

    this.greenManureFormGroup.get('cropId').updateValueAndValidity();
    this.greenManureFormGroup.get('seasonGreenManureIncorporated').updateValueAndValidity();
    this.greenManureFormGroup.get('successRate').updateValueAndValidity();
    this.greenManureFormGroup.get('nitrogenDeliveryAmount').updateValueAndValidity();
  }

  private setPrecedingCropSettingsValidator(newValidator: ValidatorFn[]) {
    this.precedingCropFormGroup.get('cropId').setValidators(newValidator);
    this.precedingCropFormGroup.get('nitrogenDeliveryAmount').setValidators(newValidator);

    this.precedingCropFormGroup.get('cropId').updateValueAndValidity();
    this.precedingCropFormGroup.get('nitrogenDeliveryAmount').updateValueAndValidity();
  }

  private updateGreenManureForm(isGreenManure: boolean) {
    if (!isGreenManure) {
      this.greenManureFormGroup.reset();
      this.setGreenManureSettingsValidator([]);
    } else {
      this.setGreenManureSettingsValidator([Validators.required]);
    }

    this.greenManureFormGroup
      .get('isNitrogenDeliveryAmountSetManually')
      .setValue(false, { emitEvent: false });
  }

  private updatePrecedingCropForm(isPrecedingCrop: boolean) {
    if (!isPrecedingCrop) {
      this.precedingCropFormGroup.reset();
      this.setPrecedingCropSettingsValidator([]);
    } else {
      this.setPrecedingCropSettingsValidator([Validators.required]);
    }
  }
}
